import { isNil, startCase } from 'lodash'
import { useParams } from 'react-router-dom'
import { useGetPatronById } from 'hooks/api/Patron/useGetPatronById'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { format, parseISO } from 'date-fns'
import { PatronDetailsHeader } from '../DetailsHeader/PatronDetailsHeader'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import { SectionHeader } from 'components/SectionHeader'
import { EditPatronInformation } from 'pages/Patrons/EditPatronInformation/EditPatronInformation'
import { useMemo, useState, type ElementType } from 'react'
import { FlagPatronAccountAsExcludedModal } from '../../ExcludePatron/FlagPatronAccountAsExcludedModal'
import { FlagPatronAccountAsDeactivatedModal } from 'pages/Patrons/DeactivatePatron/FlagPatronAccountAsDeactivatedModal'
import { getDeactivatedDate, getExcludedDate } from 'utils/util'
import { convertPhoneNumberToHex, formatPhoneNumber } from 'utils/phoneUtils'
import { ActivityButton } from 'components/ActivityButton'
import { usePutPatronTier } from 'hooks/api/Patron/usePutPatronTier'
import { useSnackbar } from 'stores/useSnackbar'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useGetTiers } from 'hooks/api/useGetTiers'
import { Counter } from 'components/Counter/Counter'
import { useGetPatronCheckInsByRange } from 'hooks/api/Patron/useGetPatronCheckInsByRange'
import { usePostManualPatronCheckin } from 'hooks/api/Patron/usePostManualPatronCheckin'
import { type Patron, type Tier } from 'types/api'
import { usePostManualPatronSweepstakeEntry } from 'hooks/api/Patron/usePostManualPatronSweepstakeEntry'
import { DropdownSweepstakes } from 'components/Shared/DropdownSweepstakes'
import { DropdownLocationSweepstakes } from 'components/Shared/DropdownLocationSweepstakes'
import { useDropdownSweepstakesStore } from 'stores/useDropdownSweepstakesStore'
import { useDropdownLocationSweepstakesStore } from 'stores/useDropdownLocationSweepstakesStore'
import { useDropdownParticipatingLocationsStore } from 'stores/useDropdownParticipatingLocationStore'
import { useDropdownLocationSweepstakesParticipatingLocationsStore } from 'stores/useDropdownLocationSweepstakesParticipatingLocationStore'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { SelectParticipatingLocation } from 'components/SelectParticipatingLocations'
import { SelectLocationSweepstakesParticipatingLocation } from 'components/SelectLocationSweepstakesParticipatingLocations'
import { SearchField } from 'components/SearchField'
import { type ApiError } from 'openapi-typescript-fetch'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { useDebounce } from '@uidotdev/usehooks'
import { colors } from 'components/ThemeProvider'
import { colorPrimitives } from 'components/Theme'
import CloseIcon from 'assets/close-filled.svg?react'
import PatronAccountsIcon from 'assets/nav_icons/patron_accounts.svg?react'
import { usePostManualPatronReferralPoints } from 'hooks/api/Patron/usePostManualPatronReferralPoints'
import { featureAccessStore } from 'stores/featureAccessStore'
import { useCurrentStateStore } from 'stores/useCurrentStateStore'
import { FlagPatronAccountAsDeleteFromStateModal } from 'pages/Patrons/RemoveFromState/FlagPatronAccountAsDeleteFromStateModal'
import { useGetPatronStatessById } from 'hooks/api/Patron/useGetPatronStatesById'

const resolveIconComponent = (iconSource: unknown): ElementType => {
  if (iconSource && typeof iconSource === 'object' && 'default' in iconSource) {
    return (iconSource as { default: ElementType }).default
  }

  return iconSource as ElementType
}

export const PatronOverviewDesktop = () => {
  const CloseIconComponent = resolveIconComponent(CloseIcon)
  const PatronAccountsIconComponent = resolveIconComponent(PatronAccountsIcon)
  const { id: patronAccountId } = useParams()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedTier, setSelectedTier] = useState<number | null>(null)
  const [checkinsToGrant, setCheckinsToGrant] = useState(0)
  const [query, setQuery] = useState('')
  const [selectedPatron, setSelectedPatron] = useState<Patron>()
  const debouncedQuery = useDebounce(query, 500)
  const { dropdownSweepstakesValue } = useDropdownSweepstakesStore()
  const { dropdownLocationSweepstakesValue } =
    useDropdownLocationSweepstakesStore()
  const { dropdownParticipatingLocationsValue } =
    useDropdownParticipatingLocationsStore()
  const { dropdownLocationSweepstakesParticipatingLocationsValue } =
    useDropdownLocationSweepstakesParticipatingLocationsStore()
  const [sweepstakeEntriesToGrant, setSweepstakeEntriesToGrant] = useState(0)
  const [
    locationSweepstakeEntriesToGrant,
    setLocationSweepstakeEntriesToGrant,
  ] = useState(0)
  const { data: patronCheckIns } = useGetPatronCheckInsByRange({
    patronId: Number(patronAccountId),
    range: 'CurrentQuarter',
  })

  const patronFeatureAllowed = featureAccessStore.getState().patron
  const { currentStateId } = useCurrentStateStore()

  const schema = yup.object({})

  const formMethods = useForm({
    resolver: yupResolver(schema),
  })

  const toggleEditModalIsOpen = async () => {
    setIsEditModalOpen(!isEditModalOpen)
  }
  const [
    isFlagPatronAccountAsExcludedModalOpen,
    setIsFlagPatronAccountAsExcludedModalOpen,
  ] = useState(false)
  const qc = useQueryClient()
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)
  const tiersQuery = useGetTiers()
  const tiers = tiersQuery.data
  const selectedTierString = useMemo(() => {
    if (isNil(selectedTier)) {
      return ''
    }

    return String(selectedTier)
  }, [selectedTier])

  const toggleFlagPatronAccountAsExcludedModalIsOpen = async () => {
    setIsFlagPatronAccountAsExcludedModalOpen(
      !isFlagPatronAccountAsExcludedModalOpen
    )
  }

  const [
    isFlagPatronAccountAsDeactivatedModalOpen,
    setIsFlagPatronAccountAsDeactivatedModalOpen,
  ] = useState(false)
  const toggleFlagPatronAccountAsDeactivatedModalIsOpen = async () => {
    setIsFlagPatronAccountAsDeactivatedModalOpen(
      !isFlagPatronAccountAsDeactivatedModalOpen
    )
  }

  const [
    isFlagPatronDeleteFromStateModalOpen,
    setIsFlagPatronDeleteFromStateModalOpen,
  ] = useState(false)
  const toggleFlagPatronDeleteFromStateModalOpen = async () => {
    setIsFlagPatronDeleteFromStateModalOpen(
      !isFlagPatronDeleteFromStateModalOpen
    )
  }

  const searchPatrons = useTypedApiClient({
    path: '/admin/patrons/search',
    method: 'get',
  })

  const handlePatronClick = (e: any) => {
    let patronId = ''
    if (e.target.nodeName === 'P') {
      const splitText = e.target.innerText.split(' ')
      patronId = splitText[splitText.length - 1]
    } else {
      patronId = e.target.value
    }
    const patronById = patronsQuery.data?.filter(
      (patron) => patron.id === Number(patronId)
    )[0]
    setSelectedPatron(patronById)
  }

  const onClosePatron = () => {
    setQuery('')
    setSelectedPatron(undefined)
  }

  const patronQuery = useGetPatronById(Number(patronAccountId))
  const { data: patronStates } = useGetPatronStatessById({
    patronId: Number(patronAccountId),
  })

  const patronsQuery = useQuery<Patron[], ApiError>({
    queryKey: ['patrons', debouncedQuery],
    queryFn: async () => {
      if (debouncedQuery.length >= 2) {
        return (
          await searchPatrons(
            {
              query: debouncedQuery,
            },
            currentStateId
              ? { headers: { 'Current-State-Id': currentStateId.toString() } }
              : undefined
          )
        ).data
      } else {
        return []
      }
    },
  })

  const putPatronMutation = usePutPatronTier({
    onError: () => {
      setSnackbarMessage(
        'An error occurred while processing changed Patron Tier',
        'error'
      )
    },
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ['/admin/patronaccount/detail'],
      })
      setSelectedTier(null)
      setSnackbarMessage('Successfully changed Patron Tier', 'success')
    },
  })

  const setCheckinsToGrantWithValidation = (amount: number) => {
    if (amount > 0) {
      setCheckinsToGrant(amount)
      return
    }

    if (Number(patronCheckIns?.length) + amount > 0) {
      setCheckinsToGrant(amount)
    }
  }

  const resetAll = () => {
    setCheckinsToGrant(0)
  }

  const { mutate: postManualPatronCheckinMutation } =
    usePostManualPatronCheckin({
      onSuccess: async () => {
        setSnackbarMessage(
          `Check-Ins successfully updated for ${patronQuery.data?.firstName} ${patronQuery.data?.lastName}.`,
          'success'
        )
        await qc.invalidateQueries({
          queryKey: [
            '/admin/patron/checkins/range',
            Number(patronAccountId),
            'CurrentQuarter',
          ],
        })
        resetAll()
      },
      onError: () => {
        setSnackbarMessage(
          `There was an error adding Check-Ins for ${patronQuery.data?.firstName} ${patronQuery.data?.lastName}.`,
          'error'
        )
      },
    })

  const {
    isPending: isManualReferralPointsMutationPending,
    mutate: postManualPatronReferralPointsMutation,
  } = usePostManualPatronReferralPoints({
    onSuccess: async () => {
      setSnackbarMessage(
        `Referral Points successfully updated for ${selectedPatron?.firstName} ${selectedPatron?.lastName}.`,
        'success'
      )
      await qc.invalidateQueries({
        queryKey: ['/admin/patrons/referralPoints/manual'],
      })
      onClosePatron()
    },
    onError: () => {
      setSnackbarMessage(
        `There was an error adding Referral Points for ${selectedPatron?.firstName} ${selectedPatron?.lastName}.`,
        'error'
      )
    },
  })

  const setSweepstakeEntriesValidation = (amount: number) => {
    if (amount > 0) {
      setSweepstakeEntriesToGrant(amount)
      return
    }

    if (Number(patronCheckIns?.length) + amount > 0) {
      setSweepstakeEntriesToGrant(amount)
    }
  }

  const resetSweepstakeEntries = () => {
    setSweepstakeEntriesToGrant(0)
    setLocationSweepstakeEntriesToGrant(0)
  }

  const { mutate: postManualPatronSweepstakeEntryMutation } =
    usePostManualPatronSweepstakeEntry({
      onSuccess: async () => {
        setSnackbarMessage(
          `Sweepstake Entries successfully updated for ${patronQuery.data?.firstName} ${patronQuery.data?.lastName}.`,
          'success'
        )
        await qc.invalidateQueries({
          queryKey: ['/admin/patron/SweepstakeEntries'],
        })
        resetSweepstakeEntries()
      },
      onError: () => {
        setSnackbarMessage(
          `There was an error adding Sweepstake Entries for ${patronQuery.data?.firstName} ${patronQuery.data?.lastName}.`,
          'error'
        )
      },
    })

  if (!patronAccountId) {
    return 'Patron Account Id cannot be null'
  }

  if (patronQuery.isPending) {
    return <ActivityIndicator />
  }

  if (patronQuery.isError || isNil(patronQuery.data)) {
    return <div>An error occurred.</div>
  }

  const createdOn = !isNil(patronQuery.data.createdOn)
    ? parseISO(patronQuery.data.createdOn)
    : null

  const dob = !isNil(patronQuery.data.dob)
    ? parseISO(patronQuery.data.dob)
    : null

  const gender = startCase(patronQuery.data?.gender)

  const mostRecentMailingAddress =
    !isNil(patronQuery.data?.addresses) && patronQuery.data.addresses.length > 0
      ? patronQuery.data?.addresses.sort((a, b) =>
          (a.id > b.id && -1) || a.id < b.id ? 1 : 0
        )[0]
      : null

  const patronStateOptions = Array.isArray(patronStates) ? patronStates : []
  const fallbackPatronStates = Array.isArray(patronQuery.data.patronStates)
    ? patronQuery.data.patronStates
    : []

  const hasOnlyOnePatronState =
    (patronStateOptions.length > 0
      ? patronStateOptions.length
      : fallbackPatronStates.length) === 1

  return (
    <PatronDetailsHeader
      patron={patronQuery.data}
      currentTab={`/PatronAccounts/${patronAccountId}/Overview`}
    >
      <div hidden={!patronQuery.data?.exclude}>
        <SectionHeader showButton={false} title="User Excluded" />
        <div className="flex flex-col border rounded p-6 mb-5">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Typography variant="body-1">
                <p className="break-words">{patronQuery.data?.excludeReason}</p>
              </Typography>
              <Typography variant="body-3">
                {getExcludedDate(patronQuery.data?.excludeDate)}
              </Typography>
            </div>
          </div>
        </div>
      </div>
      <div hidden={!patronQuery.data?.deactivated}>
        <SectionHeader showButton={false} title="Patron Deactivated" />
        <div className="flex flex-col border rounded p-6 mb-5">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Typography variant="body-1" className="break-words">
                {patronQuery.data?.deactivatedReason}
              </Typography>
              <Typography variant="body-3">
                {getDeactivatedDate(patronQuery.data?.deactivatedOn)}
              </Typography>
            </div>
          </div>
        </div>
      </div>
      <div hidden={!patronQuery.data?.pushNotificationsDisabled}>
        <SectionHeader showButton={false} title="Push Notifications Disabled" />
        <div className="flex flex-col border rounded p-6 mb-5">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Typography variant="body-1" className="break-words">
                Patron has disabled push notifications
              </Typography>
            </div>
          </div>
        </div>
      </div>
      <SectionHeader
        title="Patron Account"
        onClickButton={toggleEditModalIsOpen}
      />
      <div className="flex flex-col border rounded p-6 mb-5">
        <div className="flex flex-col justify-center space-y-4">
          <Typography variant="h4" fontWeight={'bold'}>
            Patron ID
          </Typography>
          <Typography variant="body-1">#{patronQuery.data?.id}</Typography>
        </div>
        <Divider sx={{ marginY: '24px' }} orientation="horizontal" />
        <div className="flex flex-col justify-center space-y-4">
          <Typography variant="h4" fontWeight={'bold'}>
            Name
          </Typography>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Typography variant="body-3">First Name</Typography>
              <Typography variant="body-1">
                {patronQuery.data?.firstName}
              </Typography>
            </div>
            <div>
              <Typography variant="body-3">Last Name</Typography>
              <Typography variant="body-1">
                {patronQuery.data?.lastName}
              </Typography>
            </div>
          </div>
        </div>
        <Divider sx={{ marginY: '24px' }} orientation="horizontal" />
        <div className="flex flex-col justify-center space-y-4">
          <Typography variant="h4" fontWeight={'bold'}>
            Contact
          </Typography>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Typography variant="body-3">Phone Number</Typography>
              {!isNil(patronQuery.data.phoneNumber) && (
                <Typography variant="body-1">
                  {formatPhoneNumber(patronQuery.data.phoneNumber)}
                </Typography>
              )}
            </div>
            <div>
              <Typography variant="body-3">Email</Typography>
              <Typography variant="body-1">
                {patronQuery.data?.email}
              </Typography>
            </div>
          </div>
        </div>
        <Divider sx={{ marginY: '24px' }} orientation="horizontal" />
        <div className="flex flex-col justify-center space-y-4">
          <Typography variant="h4" fontWeight={'bold'}>
            Birthdate
          </Typography>
          {!isNil(dob) && (
            <Typography variant="body-1">{`${format(dob, 'P')}`}</Typography>
          )}
        </div>
        <Divider sx={{ marginY: '24px' }} orientation="horizontal" />
        <div className="flex flex-col justify-center space-y-4">
          <Typography variant="h4" fontWeight={'bold'}>
            Gender
          </Typography>
          <Typography variant="body-1">{gender}</Typography>
        </div>
        <Divider sx={{ marginY: '24px' }} orientation="horizontal" />
        <div className="flex flex-col justify-center space-y-4">
          <Typography variant="h4" fontWeight={'bold'}>
            Mailing Address
          </Typography>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Typography variant="body-3">Address Line 1</Typography>
              <Typography variant="body-1">
                {mostRecentMailingAddress?.address1 ?? '-'}
              </Typography>
            </div>
            <div>
              <Typography variant="body-3">Address Line 2</Typography>
              <Typography variant="body-1">
                {mostRecentMailingAddress?.address2 ?? '-'}
              </Typography>
            </div>
            <div>
              <Typography variant="body-3">City</Typography>
              <Typography variant="body-1">
                {mostRecentMailingAddress?.city ?? '-'}
              </Typography>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Typography variant="body-3">State</Typography>
              <Typography variant="body-1">
                {mostRecentMailingAddress?.state ?? '-'}
              </Typography>
            </div>
            <div>
              <Typography variant="body-3">Zip</Typography>
              <Typography variant="body-1">
                {mostRecentMailingAddress?.postalCode ?? '-'}
              </Typography>
            </div>
          </div>
        </div>

        <Divider sx={{ marginY: '24px' }} orientation="horizontal" />

        <div className="flex flex-col justify-center space-y-4">
          <Typography variant="h4" fontWeight={'bold'}>
            Member Since
          </Typography>
          {!isNil(createdOn) && (
            <Typography variant="body-1">
              {`${format(createdOn, 'P')} at ${format(createdOn, 'p')}`}
            </Typography>
          )}
        </div>

        {patronFeatureAllowed && (
          <>
            <>
              <Divider sx={{ marginY: '24px' }} orientation="horizontal" />
              <Stack direction={'row'} columnGap={10}>
                <Stack direction={'column'} columnGap={10} rowGap={4}>
                  <div className="flex flex-col justify-center space-y-4">
                    <Typography variant="h4" fontWeight={'bold'}>
                      Referral Code
                    </Typography>
                    {!isNil(patronQuery.data.phoneNumber) && (
                      <Typography variant="body-1">
                        {convertPhoneNumberToHex(patronQuery.data.phoneNumber)}
                      </Typography>
                    )}
                  </div>

                  <Stack direction={'row'} columnGap={6} width={'fit-content'}>
                    <div className="flex flex-col justify-center">
                      {/* Search Patron ID or Name Section */}
                      <Typography>
                        Referred by Patron - Search Patron ID or Name
                      </Typography>
                      <Typography
                        variant="body-2"
                        color={'rgba(44, 44, 39, 0.68)'}
                      >
                        Make sure to use a minimum of at least 2 characters per
                        word when searching.
                      </Typography>
                      <SearchField
                        placeholder="Start typing to search"
                        onChange={(e) => setQuery(e.target.value)}
                        fullWidth
                      />
                      {!!patronsQuery.data?.length && !selectedPatron && (
                        <Paper
                          variant="outlined"
                          sx={{
                            mt: '7px',

                            backgroundColor: 'white',
                            zIndex: '10000',
                          }}
                        >
                          {patronsQuery.data?.map((patron) => {
                            let resultString = `${patron.firstName} ${patron.lastName} - ${patron.id}`
                            resultString = resultString.replace(
                              query,
                              `<strong>${query}</strong>`
                            )
                            return (
                              <MenuItem
                                key={patron.id}
                                value={patron.id}
                                onClick={handlePatronClick}
                              >
                                <p
                                  dangerouslySetInnerHTML={{
                                    __html: resultString,
                                  }}
                                />
                              </MenuItem>
                            )
                          })}
                        </Paper>
                      )}
                      {selectedPatron && (
                        <Box
                          sx={{
                            backgroundColor: colorPrimitives.lightGray,
                            p: '12px',
                          }}
                          maxWidth={'520px'}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex flex-row items-center">
                              <SvgIcon
                                sx={{
                                  color: colors.text.primary,
                                  transform: 'translate(0, -2px)',
                                  height: '40px',
                                  width: '40px',
                                  marginRight: '10px',
                                }}
                              >
                                {PatronAccountsIconComponent ? (
                                  <PatronAccountsIconComponent />
                                ) : null}
                              </SvgIcon>
                              <div className="flex flex-col">
                                <Typography variant="body-1">
                                  {selectedPatron?.firstName}{' '}
                                  {selectedPatron?.lastName}
                                </Typography>
                                <Typography
                                  variant="body-1"
                                  pb={1}
                                  color="text.secondary"
                                >
                                  {selectedPatron?.id}
                                </Typography>
                              </div>
                            </div>
                            <Box
                              onClick={onClosePatron}
                              sx={{
                                '&:hover': {
                                  cursor: 'pointer',
                                },
                              }}
                            >
                              <SvgIcon
                                sx={{
                                  transform: 'translate(0, -2px)',
                                  height: '24px',
                                  width: '24px',
                                }}
                              >
                                {CloseIconComponent ? (
                                  <CloseIconComponent />
                                ) : null}
                              </SvgIcon>
                            </Box>
                          </div>
                        </Box>
                      )}
                    </div>
                    <Stack
                      direction={'row'}
                      height="50px"
                      mt="48px"
                      flexWrap={'wrap'}
                    >
                      <Stack direction={'row'} gap={2} alignItems={'center'}>
                        <ActivityButton
                          variant="contained"
                          disabled={!selectedPatron}
                          active={isManualReferralPointsMutationPending}
                          onClick={() => {
                            postManualPatronReferralPointsMutation({
                              patronId: Number(patronAccountId),
                              referredByPatronId: Number(selectedPatron?.id),
                            })
                          }}
                        >
                          Grant Referral Points
                        </ActivityButton>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </>
            <Divider sx={{ marginY: '24px' }} orientation="horizontal" />
            <div className="flex flex-col justify-center">
              <Typography variant="h4" fontWeight={'bold'}>
                Tier
              </Typography>

              <Stack
                direction={'row'}
                alignItems="top"
                flexWrap={'wrap'}
                columnGap={7}
              >
                <Stack direction={'column'}>
                  <Typography variant="body-1" mt={2}>
                    Current Tier:{' '}
                    {patronQuery.data.currentTier?.tier?.type ?? 'Tier Not Set'}
                  </Typography>
                  <Stack direction={'row'} gap={'38px'} alignItems={'center'}>
                    <Select
                      sx={{ minWidth: 150 }}
                      value={selectedTierString}
                      onChange={(event) =>
                        setSelectedTier(Number(event.target.value))
                      }
                    >
                      {Array.isArray(tiers) &&
                        tiers?.map((tier: Tier) => (
                          <MenuItem
                            key={tier.id}
                            value={tier.id}
                            disabled={
                              tier.id === patronQuery.data.currentTier?.tier?.id
                            }
                          >
                            {tier.type}{' '}
                            {tier.id === patronQuery.data.currentTier?.tier?.id
                              ? '(Current)'
                              : ''}
                          </MenuItem>
                        ))}
                    </Select>
                    <ActivityButton
                      variant="contained"
                      disabled={isNil(selectedTier)}
                      active={putPatronMutation.isPending}
                      onClick={async () => {
                        if (isNil(selectedTier)) {
                          throw new Error("Selected Tier can't be null")
                        }
                        await putPatronMutation.mutateAsync({
                          id: patronQuery.data.id,
                          tierId: selectedTier,
                        })
                      }}
                    >
                      Update Tier
                    </ActivityButton>
                  </Stack>
                </Stack>

                <Stack
                  direction={'row'}
                  alignItems={'end'}
                  columnGap={0}
                  rowGap={2}
                  flexWrap={'wrap'}
                >
                  <Stack direction={'column'}>
                    <Typography variant="body-1" mt={2}>
                      Current Check-in Count: {patronCheckIns?.length}
                    </Typography>
                    <Stack direction={'row'} gap={2} alignItems={'center'}>
                      <Counter
                        amount={checkinsToGrant}
                        setAmount={setCheckinsToGrantWithValidation}
                        minimumAmount={0}
                      />
                      <ActivityButton
                        variant="contained"
                        disabled={checkinsToGrant === 0}
                        active={putPatronMutation.isPending}
                        onClick={() => {
                          postManualPatronCheckinMutation({
                            patronId: Number(patronAccountId),
                            numCheckins: checkinsToGrant,
                          })
                        }}
                      >
                        Update Check-in Count
                      </ActivityButton>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>

              <Typography variant="body-1" mt={2}>
                Patron Entries into a Selected Sweepstake
              </Typography>
              <Stack
                direction={'row'}
                columnGap={7}
                rowGap={4}
                alignItems={'end'}
                flexWrap={'wrap'}
              >
                <Stack direction={'column'} rowGap={1}>
                  <Stack>
                    <DropdownSweepstakes liveOnly={true} />
                    <form>
                      <FormProvider {...formMethods}>
                        <SelectParticipatingLocation
                          placeholder="Select Location"
                          contestId={Number(dropdownSweepstakesValue)}
                        />
                      </FormProvider>
                    </form>
                  </Stack>
                  <Stack direction={'column'} gap={2} alignItems={'center'}>
                    <Stack direction={'row'} gap={2} alignItems={'center'}>
                      <Counter
                        amount={sweepstakeEntriesToGrant}
                        setAmount={setSweepstakeEntriesValidation}
                        minimumAmount={0}
                      />
                      <ActivityButton
                        variant="contained"
                        disabled={
                          sweepstakeEntriesToGrant === 0 ||
                          dropdownSweepstakesValue === '' ||
                          dropdownParticipatingLocationsValue === ''
                        }
                        active={putPatronMutation.isPending}
                        onClick={() => {
                          postManualPatronSweepstakeEntryMutation({
                            patronId: Number(patronAccountId),
                            sweepstakeId: Number(dropdownSweepstakesValue),
                            licensedEstablishmentId: Number(
                              dropdownParticipatingLocationsValue
                            ),
                            numSweepstakeEntries: Number(
                              sweepstakeEntriesToGrant
                            ),
                            patronCheckInId: null,
                          })
                        }}
                      >
                        Update
                      </ActivityButton>
                    </Stack>
                  </Stack>
                </Stack>
                <Stack direction={'column'} rowGap={1}>
                  <Stack>
                    <DropdownLocationSweepstakes liveOnly={true} />
                    <form>
                      <FormProvider {...formMethods}>
                        <SelectLocationSweepstakesParticipatingLocation
                          placeholder="Select Location"
                          contestId={Number(dropdownLocationSweepstakesValue)}
                        />
                      </FormProvider>
                    </form>
                  </Stack>
                  <Stack direction={'column'} gap={2} alignItems={'center'}>
                    <Stack direction={'row'} gap={2} alignItems={'center'}>
                      <Counter
                        amount={locationSweepstakeEntriesToGrant}
                        setAmount={setLocationSweepstakeEntriesToGrant}
                        minimumAmount={0}
                      />
                      <ActivityButton
                        variant="contained"
                        disabled={
                          locationSweepstakeEntriesToGrant === 0 ||
                          dropdownLocationSweepstakesValue === '' ||
                          dropdownLocationSweepstakesParticipatingLocationsValue ===
                            ''
                        }
                        active={putPatronMutation.isPending}
                        onClick={() => {
                          postManualPatronSweepstakeEntryMutation({
                            patronId: Number(patronAccountId),
                            sweepstakeId: Number(
                              dropdownLocationSweepstakesValue
                            ),
                            licensedEstablishmentId: Number(
                              dropdownLocationSweepstakesParticipatingLocationsValue
                            ),
                            numSweepstakeEntries: Number(
                              locationSweepstakeEntriesToGrant
                            ),
                            patronCheckInId: null,
                          })
                        }}
                      >
                        Update
                      </ActivityButton>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </div>
          </>
        )}
      </div>
      <div>
        <Button
          onClick={() => {
            setIsFlagPatronAccountAsExcludedModalOpen(
              !isFlagPatronAccountAsExcludedModalOpen
            )
          }}
        >
          {!patronQuery.data.exclude
            ? 'Flag Patron Account as Excluded'
            : 'Unflag Patron Account to Include'}
        </Button>
      </div>
      <div>
        <Button
          onClick={() => {
            setIsFlagPatronAccountAsDeactivatedModalOpen(
              !isFlagPatronAccountAsDeactivatedModalOpen
            )
          }}
        >
          {!patronQuery.data.deactivated
            ? 'Deactivate Patron'
            : 'Reactivate Patron'}
        </Button>
      </div>
      {!hasOnlyOnePatronState && (
        <Button
          onClick={() => {
            setIsFlagPatronDeleteFromStateModalOpen(
              !isFlagPatronDeleteFromStateModalOpen
            )
          }}
        >
          Delete from State
        </Button>
      )}

      <EditPatronInformation
        isModalOpen={isEditModalOpen}
        toggleModalIsOpen={toggleEditModalIsOpen}
        patron={patronQuery.data}
      />
      <FlagPatronAccountAsExcludedModal
        isModalOpen={isFlagPatronAccountAsExcludedModalOpen}
        toggleModalIsOpen={toggleFlagPatronAccountAsExcludedModalIsOpen}
        toggleIsOpen={toggleFlagPatronAccountAsExcludedModalIsOpen}
        patron={patronQuery.data}
      />

      <FlagPatronAccountAsDeactivatedModal
        isModalOpen={isFlagPatronAccountAsDeactivatedModalOpen}
        toggleModalIsOpen={toggleFlagPatronAccountAsDeactivatedModalIsOpen}
        toggleIsOpen={toggleFlagPatronAccountAsDeactivatedModalIsOpen}
        patron={patronQuery.data}
      />

      <FlagPatronAccountAsDeleteFromStateModal
        isModalOpen={isFlagPatronDeleteFromStateModalOpen}
        toggleModalIsOpen={toggleFlagPatronDeleteFromStateModalOpen}
        toggleIsOpen={toggleFlagPatronDeleteFromStateModalOpen}
        patron={patronQuery.data}
        states={patronStateOptions}
      />
    </PatronDetailsHeader>
  )
}
